<template>
  <div
    v-if="application.rows && application.rows.length == 0"
    class="text-starttitle">
    {{ $t('Common.NoData') }}
  </div>
  <v-skeleton-loader
    v-else-if="loading"
    :boilerplate="false"
    class="mt-4"
    :tile="false"
    type="table-thead, table-tbody, table-tbody"
    width="100%"/>
  <div
    v-else
    class="px-0 pb-0">
    <template v-for="(groupItem, groupItemIndex) in groups">
      <CollapseBlock
        v-if="groupItem.items && groupItem.items.length > 0"
        :key="groupItem.name + (groupItem.items ? groupItem.items.length : '')"
        class="width-100"
        transparent
        :value.sync="groupShow[groupItemIndex]">
        <div
          v-if="groupItem.items && groupItem.items.length > 0 && hasChecked && groupItem.name"
          slot="title"
          class="py-5 flex justify-space-between align-center"
          style="border-top: 1px solid rgba(var(--black-color-rgba), 0.12);">
          {{ groupItem.name }}
          <span class="secondary__green_not_active--text subtitle-2 pr-1">
            {{ groupItem.items && groupItem.items.length }}
          </span>
        </div>
        <v-data-table
          v-model="localSelected"
          calculate-widths
          disable-pagination
          fixed-header
          :headers="headers"
          hide-default-footer
          :items="groupItem.items"
          :no-data-text="$t('Tenders.NoData')"
          show-select
          @click:row="select">
          <template #[`groupItem.resourceName`]="{ item }">
            <ResourceName :item="item"/>
          </template>
          <template #[`item.approvedQuantity`]="{ item }">
            <div
              :key="item.approvedQuantity + '' + item.id"
              class="flex flex-wrap flex-direction-column">
              <span>
                {{ formatNumber(item.approvedQuantity || item.declaredQuantity) }}
                {{ item.unit ? item.unit.name || item.unit.fullName : $t('Common.NoData') }}
              </span>

              <sTooltip
                v-if="item.declaredQuantity != item.approvedQuantity && item.approvedQuantity"
                :border="false"
                :content="`${$t('Application.Declared')} ${item.declaredQuantity}`"
                type="white">
                <span
                  class="changed-item-text secondary__green_not_active--text">
                  {{ $t('Application.Changed') }}
                </span>
              </sTooltip>
            </div>
          </template>
          <template #[`item.approvedSupplyDate`]="{ item }">
            <div
              :key="item.approvedSupplyDate || item.approvedPriority.id + '' + item.id"
              class="flex flex-direction-column">
              <span>
                <DateSpan
                  date="approvedSupplyDate"
                  :item="item"/>
              </span>
              <span
                v-if="isDateChanged(item)"
                class="changed-item-text secondary__green_not_active--text">
                {{ $t('Application.Changed') }}
              </span>
            </div>
          </template>
          <template #[`item.price`]="{ item }">
            <div v-if="item.price">
              {{ formatNumber(item.price) }}
              {{ getCurrencyName(application, item.price) }}
            </div>
            <span v-else> - </span>
          </template>
          <template #[`item.summa`]="{ item }">
            <div v-if="sum(item)">
              {{ formatNumber(sum(item)) }}
              {{ getCurrencyName(application, sum(item)) }}
            </div>
            <span v-else> - </span>
          </template>
          <template #[`item.comments`]="{ item }">
            <CommentsCountBtn
              :chat="item.chat"
              :comments_length="item.chat && item.chat.messages ? item.chat.messages.length : 0"
              @action="onlyChat(item)"/>
          </template>
          <template #[`item.tender`]="{ item }">
            <router-link
              v-if="item.tender"
              :to="{ name: 'tenderitem', params: { id: item.tender.tenderId, org: $route.params.org } }">
              <StatusChip
                key-name="id"
                object
                :status="item.tender.tenderStatus"
                value-name="title"/>
            </router-link>
            <span v-else>–</span>
          </template>
        </v-data-table>
      </CollapseBlock>
    </template>
  </div>
</template>

<script>
import ResourceName from 'components/purchasing/ResourceName.vue'
import DateSpan from 'components/common/elements/DateSpan.vue'
import CommentsCountBtn from 'components/common/elements/CommentsCountBtn.vue'

import StatusChip from 'components/common/elements/StatusChip.vue'
import CollapseBlock from 'components/common/CollapseBlock.vue'

import { filterSortMixin, modalMixin } from 'mixins'

export default {
  name: 'PurchaseRows',
  mixins: [filterSortMixin, modalMixin],
  data() {
    return {
      groupShow: [],
      headers: [
        { text: '', value: 'data-table-select', width: '40px' },
        { text: this.$t('Purchasing.Goods'), value: 'resourceName', width: '22%' },
        { text: this.$t('Tenders.AdditionalDescription'), value: 'description', sortable: false, width: '17%' },
        { text: this.$t('Catalog.Amount'), value: 'approvedQuantity', width: '10%' },
        { text: this.$t('Catalog.DeliveryDate'), value: 'approvedSupplyDate', width: '12%' },
        { text: this.$t('Catalog.Price'), value: 'price', width: '12%' },
        { text: this.$t('Catalog.TotalCost'), value: 'summa', width: '12%' },
        { text: this.$t('Purchasing.TenderStatus'), value: 'tender', width: '15%' },
        { text: '', value: 'comments', sortable: false, width: '44px' },
      ],
    }
  },
  props: {
    application: Object,
    loading: Boolean,
    selected: Array
  },
  computed: {
    groups() {
      if (this.loading) {
        return []
      }
      let rows = this.application.rows || [],
        groups = new Set(),
        findEtatus = (array) => {
          return array.find(it => it.userId && this.user_id && it?.userId?.trim() == this.user_id)
        }
      rows.forEach(el => {
        if (el.applicationRowStatuses) {
          let status = findEtatus(el.applicationRowStatuses)
          status ? groups.add(status.statusRowId) : groups.add('')
        }
      })
      groups = [...groups].map(el => ({
        name: '',
        items: [],
        code: el
      }))
      if (!groups.length) {
        groups = [{
          name: '',
          items: rows,
          code: ''
        }]
        return groups
      }
      rows.forEach(el => {
        let status
        if (el.applicationRowStatuses?.length) {
          status = findEtatus(el.applicationRowStatuses)
        }
        if (status) {
          let group = groups.find(it => it.code == status.statusRowId)
          group.items.push(el)
          group.name = status.statusRow ? status.statusRow.name :
            status.statusRowId == '+' ? this.$t('Application.Success') :
              status.statusRowId == '–' ? this.$t('Application.Decline') : ''
        } else {
          let group = groups.find(it => !it.code)
          group.items.push(el)
          group.name = this.$t('Tenders.PENDING')
        }
      })
      return groups
    },
    localSelected: {
      get() {
        return [...this.selected]
      },
      set(newVal) {
        this.$emit('update:selected', newVal)
      }
    }
  },
  methods: {
    hasChecked() {
      return !!this.groups?.some(el => el.code != '')
    },
    isDateChanged(item) {
      return (
        item.approvedSupplyDate !== item.declaredSupplyDate && item.declaredSupplyDate ||
        item.declaredPriority.id !== item.approvedPriority.id
      )
    },
    onlyChat(item) {
      item.only_chat = true
    },
    select(item, options) {
      options.select(!options.isSelected)
      this.localSelected = [...options.selectedItems] // Обновляем через computed
    }
  },
  components: {
    ResourceName,
    DateSpan,
    CommentsCountBtn,
    StatusChip,
    CollapseBlock
  }
}
</script>

<style scoped>
.changed-item-text {
  font-size: 11px;
  line-height: 12px;
}
</style>
